import { Text, Icon, Card, Skeleton } from "@growtherapy/sprout-ui";
import { useAtomValue, useSetAtom } from "jotai";
import {
  drawerStateAtom,
  ProviderDrawerState,
  selectedClientInfoAtom,
} from "../state";
import { faAngleRight } from "@fortawesome/pro-solid-svg-icons";
import { twMerge } from "tailwind-merge";
import { PROGRESS_NOTE_CTA, PROGRESS_NOTE_SECTION_TITLE } from "./constants";
import { useGetPatientByShortId } from "../useGetPatientByShortId";
import { formatRange } from "./utils";
import { NoteTypeEnum } from "@grow-therapy-team/seedling-components";

export type ProgressNoteSectionProps = {
  className?: string;
  state?: "loading" | "error" | "default";
  startTime: Date | null;
  endTime: Date | null;
  onClick: () => void;
};

function LoadingSkeleton() {
  return <Skeleton className="h-10 w-full m-0" />;
}

function ProgressNoteSectionButton({
  onClick,
  startTime,
  endTime,
}: Pick<ProgressNoteSectionProps, "onClick" | "endTime" | "startTime">) {
  const hasStartAndEndTime = !!startTime && !!endTime;
  const duration = hasStartAndEndTime && formatRange(startTime, endTime);

  return (
    <button
      className="relative flex w-full justify-between items-center text-start hover:bg-neutral-400"
      aria-label="Open progress note"
      onClick={onClick}
    >
      <div>
        {duration && (
          <Text
            as="span"
            className="block text-neutral-700 font-medium"
            variant="label"
          >
            {duration}
          </Text>
        )}
        <Text as="span" className="block">
          {PROGRESS_NOTE_CTA}
        </Text>
      </div>
      <Icon
        icon={faAngleRight}
        className="mr-2 flex items-center justify-center"
      />
    </button>
  );
}

function ProgressNoteSectionContent({
  state,
  ...buttonProps
}: Omit<ProgressNoteSectionProps, "className">) {
  switch (state) {
    case "loading":
      return <LoadingSkeleton />;
    case "error":
      return (
        <Text className="text-coral-700">Error loading progress note</Text>
      );
    default:
      return <ProgressNoteSectionButton {...buttonProps} />;
  }
}

export function ProgressNoteSection({
  className,
  ...contentProps
}: ProgressNoteSectionProps) {
  return (
    <Card
      title={PROGRESS_NOTE_SECTION_TITLE}
      titleAs="h3"
      className={twMerge("flex flex-col m-0", className)}
      data-testid="progress-note-section"
    >
      <ProgressNoteSectionContent {...contentProps} />
    </Card>
  );
}

export function ProgressNoteSectionWrapper({
  className,
}: {
  className?: string;
}) {
  const selectedPatientShortId = useAtomValue(
    selectedClientInfoAtom,
  )?.patientShortId;

  const {
    loading: isLoading,
    data: patientChartData,
    error,
  } = useGetPatientByShortId(selectedPatientShortId);
  const lastAppointment = patientChartData?.patientChart?.lastAppointment;
  const startTime = lastAppointment?.timeStart
    ? new Date(lastAppointment.timeStart as string)
    : null;
  const endTime = lastAppointment?.timeEnd
    ? new Date(lastAppointment.timeEnd as string)
    : null;

  const setDrawerState = useSetAtom(drawerStateAtom);
  const openProgressNote = () => {
    setDrawerState(ProviderDrawerState.PROGRESS_NOTES);
  };

  const notes = patientChartData?.patientChart?.lastAppointment?.notes;
  const noteType: NoteTypeEnum = notes?.[0]?.noteType ?? ("" as NoteTypeEnum);

  const isProgressNote = noteType === NoteTypeEnum.GROW_PROGRESS;

  const state = isLoading ? "loading" : error ? "error" : "default";

  if (notes && !isProgressNote) {
    return null;
  }

  return (
    <ProgressNoteSection
      className={className}
      state={state}
      onClick={openProgressNote}
      startTime={startTime}
      endTime={endTime}
    />
  );
}
